import { useEffect, useState } from 'react';
import { getAllAnnouncements, createAnnouncement, updateAnnouncement } from '../../services/apiClient';
import type { Announcement } from '../../types';
import { formatShortDate } from '../../lib/formatting';
import { StatusBadge } from '../../components/ui/StatusBadge';
import { Button } from '../../components/ui/Button';
import { EmptyState } from '../../components/ui/EmptyState';
import { Card } from '../../components/ui/Card';
import styles from './ManageNews.module.css';

type FormState = {
  title: string;
  body: string;
  published: boolean;
  publish_date: string;
};

const EMPTY_FORM: FormState = {
  title: '',
  body: '',
  published: false,
  publish_date: new Date().toISOString().slice(0, 10),
};

export function ManageNews() {
  const [announcements, setAnnouncements] = useState<Announcement[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState<Record<string, string>>({});

  const load = () => {
    getAllAnnouncements().then((r) => {
      if (r.data) setAnnouncements(r.data);
      setLoading(false);
    });
  };

  useEffect(() => { load(); }, []);

  const openNew = () => {
    setForm(EMPTY_FORM);
    setEditId(null);
    setErrors({});
    setShowForm(true);
  };

  const openEdit = (ann: Announcement) => {
    setForm({
      title: ann.title,
      body: ann.body,
      published: ann.published,
      publish_date: ann.publish_date.slice(0, 10),
    });
    setEditId(ann.id);
    setErrors({});
    setShowForm(true);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    const checked = type === 'checkbox' ? (e.target as HTMLInputElement).checked : undefined;
    setForm((prev) => ({ ...prev, [name]: type === 'checkbox' ? checked : value }));
    setErrors((prev) => ({ ...prev, [name]: '' }));
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.title.trim()) errs.title = 'Title is required';
    if (!form.body.trim()) errs.body = 'Body is required';
    return errs;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length > 0) { setErrors(errs); return; }
    setSaving(true);
    if (editId) {
      await updateAnnouncement(editId, {
        ...form,
        publish_date: new Date(form.publish_date).toISOString(),
      });
    } else {
      await createAnnouncement({
        ...form,
        publish_date: new Date(form.publish_date).toISOString(),
      });
    }
    setSaving(false);
    setShowForm(false);
    load();
  };

  const togglePublished = async (ann: Announcement) => {
    await updateAnnouncement(ann.id, { published: !ann.published });
    load();
  };

  return (
    <div className={styles.page}>
      <div className={styles.toolbar}>
        <p className={styles.count}>
          {announcements.length} announcement{announcements.length !== 1 ? 's' : ''}
        </p>
        <Button size="sm" onClick={openNew}>+ New Announcement</Button>
      </div>

      {showForm && (
        <Card padding="md">
          <h2 className={styles.formTitle}>{editId ? 'Edit Announcement' : 'New Announcement'}</h2>
          <form onSubmit={handleSubmit} noValidate className={styles.form}>
            <div className={styles.field}>
              <label htmlFor="ann-title" className={styles.label}>Title <span className={styles.req}>*</span></label>
              <input id="ann-title" name="title" type="text" value={form.title} onChange={handleChange}
                className={[styles.input, errors.title ? styles.inputError : ''].join(' ')} />
              {errors.title && <p className={styles.error}>{errors.title}</p>}
            </div>

            <div className={styles.field}>
              <label htmlFor="ann-body" className={styles.label}>Body <span className={styles.req}>*</span></label>
              <textarea id="ann-body" name="body" rows={5} value={form.body} onChange={handleChange}
                className={[styles.textarea, errors.body ? styles.inputError : ''].join(' ')} />
              {errors.body && <p className={styles.error}>{errors.body}</p>}
            </div>

            <div className={styles.formRow}>
              <div className={styles.field}>
                <label htmlFor="ann-date" className={styles.label}>Publish Date</label>
                <input id="ann-date" name="publish_date" type="date" value={form.publish_date}
                  onChange={handleChange} className={styles.input} />
              </div>
              <div className={styles.field}>
                <label className={styles.checkboxLabel}>
                  <input type="checkbox" name="published" checked={form.published} onChange={handleChange} className={styles.checkbox} />
                  Published (visible on public site)
                </label>
              </div>
            </div>

            <div className={styles.formActions}>
              <Button type="submit" loading={saving}>
                {editId ? 'Save Changes' : 'Create Announcement'}
              </Button>
              <Button type="button" variant="ghost" onClick={() => setShowForm(false)}>Cancel</Button>
            </div>
          </form>
        </Card>
      )}

      {loading ? (
        <p className={styles.loading}>Loading…</p>
      ) : announcements.length === 0 ? (
        <EmptyState title="No announcements yet" description="Create your first announcement to display news on the public site." />
      ) : (
        <div className={styles.list}>
          {announcements.map((ann) => (
            <div key={ann.id} className={styles.item}>
              <div className={styles.itemInfo}>
                <div className={styles.itemMeta}>
                  <StatusBadge label={ann.published ? 'published' : 'draft'} />
                  <span className={styles.itemDate}>{formatShortDate(ann.publish_date)}</span>
                </div>
                <p className={styles.itemTitle}>{ann.title}</p>
                <p className={styles.itemBody}>{ann.body.slice(0, 120)}{ann.body.length > 120 ? '…' : ''}</p>
              </div>
              <div className={styles.itemActions}>
                <button className={styles.actionBtn} onClick={() => openEdit(ann)}>Edit</button>
                <button className={styles.actionBtn} onClick={() => togglePublished(ann)}>
                  {ann.published ? 'Unpublish' : 'Publish'}
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
